import React from 'react';
import { useLingui } from '@lingui/react/macro';

import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import useI18nContext from '@rover/react-lib/src/context/useI18nContext';
import daDk from '@rover/react-lib/src/images/homepage/store-button/android/da-dk.svg?url';
import deAt from '@rover/react-lib/src/images/homepage/store-button/android/de-at.svg?url';
import deCh from '@rover/react-lib/src/images/homepage/store-button/android/de-ch.svg?url';
import deDe from '@rover/react-lib/src/images/homepage/store-button/android/de-de.svg?url';
import enCa from '@rover/react-lib/src/images/homepage/store-button/android/en-ca.svg?url';
import enGb from '@rover/react-lib/src/images/homepage/store-button/android/en-gb.svg?url';
import enIe from '@rover/react-lib/src/images/homepage/store-button/android/en-ie.svg?url';
import enUs from '@rover/react-lib/src/images/homepage/store-button/android/en-us.svg?url';
import esEs from '@rover/react-lib/src/images/homepage/store-button/android/es-es.svg?url';
import fiFi from '@rover/react-lib/src/images/homepage/store-button/android/fi-fi.svg?url';
import frCa from '@rover/react-lib/src/images/homepage/store-button/android/fr-ca.svg?url';
import frFr from '@rover/react-lib/src/images/homepage/store-button/android/fr-fr.svg?url';
import itIt from '@rover/react-lib/src/images/homepage/store-button/android/it-it.svg?url';
import nbNo from '@rover/react-lib/src/images/homepage/store-button/android/nb-no.svg?url';
import nlBe from '@rover/react-lib/src/images/homepage/store-button/android/nl-be.svg?url';
import nlNl from '@rover/react-lib/src/images/homepage/store-button/android/nl-nl.svg?url';
import plPl from '@rover/react-lib/src/images/homepage/store-button/android/pl-pl.svg?url';
import ptPt from '@rover/react-lib/src/images/homepage/store-button/android/pt-pt.svg?url';
import svSe from '@rover/react-lib/src/images/homepage/store-button/android/sv-se.svg?url';
import getBrandSpecificValue from '@rover/shared/js/utils/getBrandSpecificValue';
import { getPath } from '@rover/shared/js/utils/getFullUrl';
import { getCountryUrlPrefix, US_COUNTRY_URL_PREFIX } from '@rover/utilities/roverGeo.constants';

import type { Props as AppStoreButtonProps } from './AppStoreButton';
import AppStoreButton from './AppStoreButton';

export const GOOGLE_PLAY_STORE_LINK = (country: string, language: string): string =>
  `http://play.google.com/store/apps/details?id=com.rover.android&gl=${country}&hl=${language}`;

export const CIAF_GOOGLE_PLAY_STORE_LINK = (country: string, language: string): string =>
  `https://play.google.com/store/apps/details?id=com.catinaflat.webapp&gl=${country}&hl=${language}`;

const images = {
  [Locale.EnGb]: enGb,
  [Locale.EnIe]: enIe,
  [Locale.EnUs]: enUs,
  [Locale.EnCa]: enCa,
  [Locale.EsEs]: esEs,
  [Locale.DaDk]: daDk,
  [Locale.DeAt]: deAt,
  [Locale.DeCh]: deCh,
  [Locale.DeDe]: deDe,
  [Locale.FiFi]: fiFi,
  [Locale.ItIt]: itIt,
  [Locale.FrFr]: frFr,
  [Locale.FrCa]: frCa,
  [Locale.SvSe]: svSe,
  [Locale.NlBe]: nlBe,
  [Locale.NlNl]: nlNl,
  [Locale.NbNo]: nbNo,
  [Locale.PlPl]: plPl,
  // pt-pt has no Locale member
  'pt-pt': ptPt,
};

export type Props = Omit<
  AppStoreButtonProps,
  'ariaLabel' | 'os' | 'imageSrc' | 'altText' | 'linkPath' | 'buttonProps'
> & {
  linkPath?: string;
};

const AndroidAppStoreButton = ({ linkPath, ...others }: Props): JSX.Element => {
  const { t } = useLingui();
  const { locale } = useI18nContext();
  const image = images[locale] || enUs;
  const language = locale.slice(0, 2);
  const requestUrlPath = getPath();
  const country = getCountryUrlPrefix(requestUrlPath) ?? US_COUNTRY_URL_PREFIX;
  const defaultLink = getBrandSpecificValue({
    rover: GOOGLE_PLAY_STORE_LINK(country, language),
    catInAFlat: CIAF_GOOGLE_PLAY_STORE_LINK(country, language),
  });
  return (
    <AppStoreButton
      {...others}
      os="android"
      imageSrc={image}
      altText={t`Get it on Google Play Badge`}
      linkPath={linkPath || defaultLink}
      ariaLabel={t`Get it on Google Play`}
    />
  );
};

export default AndroidAppStoreButton;
